import { useEffect, useState } from "react";
import { addDays, parseISO } from "date-fns";
import { Smile, Meh, Frown, Zap, BatteryLow, HeartCrack, Droplet, Trash2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtDate, iso } from "@/lib/format";

export const MOODS = [
  ["happy", "Bahagia", Smile], ["calm", "Biasa", Meh], ["pms", "PMS", HeartCrack],
  ["cramps", "Nyeri", Frown], ["energy", "Berenergi", Zap], ["tired", "Lelah", BatteryLow],
];
const STATUS = [["work", "Kerja"], ["piket", "Piket"], ["off", "Libur"]];
const CATS = [["konser", "Konser"], ["f1", "F1"], ["fanmeet", "Jumpa fans"], ["festival", "Festival"], ["film", "Film/Drama"], ["lainnya", "Lainnya"]];

const findPeriod = (periods, date) =>
  periods.find((p) => date >= p.date && date <= (p.end || iso(addDays(parseISO(p.date), 9))));

const EventMini = ({ date, events, onAdd, onDelete }) => {
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState("lainnya");
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-pink-600">Acara hari ini</p>
      {events.map((e) => (
        <div key={e.id} className="flex items-center gap-2 text-sm bg-violet-50 rounded-2xl px-3 py-2" data-testid="day-event-item">
          <span className="flex-1 text-gray-700">{e.title}{e.source === "ai" && <span className="text-[10px] text-violet-500 ml-1">(otomatis)</span>}</span>
          {e.source !== "birthday" && <button onClick={() => onDelete(e.id)} className="text-gray-300 hover:text-rose-500" data-testid="day-event-delete" aria-label="Hapus acara"><Trash2 className="w-4 h-4" /></button>}
        </div>
      ))}
      <div className="flex gap-2">
        <input className="field !h-10" placeholder="Tambah acara/agenda" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="day-event-title" />
        <select className="field !h-10 !w-28" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="day-event-cat">
          {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button disabled={!title} onClick={async () => { await onAdd({ title, date, category: cat }); setTitle(""); }} className="px-3 rounded-2xl bg-violet-100 text-violet-600 disabled:opacity-50" data-testid="day-event-add" aria-label="Tambah acara"><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const DayDialog = ({ date, onClose, day, periods, events, onSaveDay, periodsApi, eventsApi, weeklyPiket }) => {
  const [status, setStatus] = useState("default");
  const [mood, setMood] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    setStatus(day?.status || "default");
    setMood(day?.mood || "");
    setNote(day?.note || "");
  }, [day, date]);

  if (!date) return null;
  const eff = status === "off" ? "off" : status === "piket" || (status === "default" && weeklyPiket) ? "piket" : "work";
  const pick = (k) => setStatus(k === "off" ? "off" : k === "piket" ? (weeklyPiket ? "default" : "piket") : (weeklyPiket ? "work" : "default"));
  const period = findPeriod(periods, date);
  const save = async () => { await onSaveDay(date, { status, mood, note }); onClose(); };
  const withSave = (fn) => async () => { await onSaveDay(date, { status, mood, note }); await fn(); onClose(); };

  return (
    <Dialog open={!!date} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="day-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-gray-800">{fmtDate(date, "EEEE, d MMMM yyyy")}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <p className="text-xs font-semibold text-pink-600">Jadwal kerja</p>
            <div className="flex gap-2 flex-wrap">
              {STATUS.map(([v, l]) => <button key={v} disabled={v === "off" && eff === "piket"} onClick={() => pick(v)} className={`chip disabled:opacity-40 disabled:cursor-not-allowed ${eff === v ? "chip-on" : "chip-off"}`} data-testid={`day-status-${v}`}>{l}</button>)}
            </div>
            {eff === "piket" && <p className="text-[11px] text-amber-600" data-testid="day-piket-note">Hari piket tidak bisa diambil libur. Kalau tukar jadwal, pilih "Kerja" dulu lalu tandai piket di hari lain.</p>}
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-pink-600">Mood</p>
            <div className="grid grid-cols-3 gap-2">
              {MOODS.map(([v, l, I]) => (
                <button key={v} onClick={() => setMood(mood === v ? "" : v)} data-testid={`mood-selector-${v}`} className={`rounded-2xl border py-2.5 flex flex-col items-center gap-1 text-xs font-medium transition-colors ${mood === v ? "bg-pink-500 text-white border-pink-500" : "border-pink-100 text-gray-600 hover:bg-pink-50"}`}>
                  <I className="w-5 h-5" /> {l}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xs font-semibold text-pink-600">Menstruasi</p>
            <div className="flex gap-2 flex-wrap">
              {!period && <Button variant="secondary" className="rounded-full" data-testid="period-start-btn" onClick={withSave(() => periodsApi.add({ date }))}><Droplet className="w-4 h-4 mr-1" /> Mens mulai hari ini</Button>}
              {period && period.date !== date && <Button variant="secondary" className="rounded-full" data-testid="period-end-btn" onClick={withSave(() => periodsApi.update(period.id, { date: period.date, end: date, note: period.note || "" }))}>Mens selesai hari ini</Button>}
              {period && <Button variant="ghost" className="rounded-full text-rose-500" data-testid="period-delete-btn" onClick={withSave(() => periodsApi.remove(period.id))}>Hapus catatan mens ({fmtDate(period.date, "d MMM")})</Button>}
            </div>
          </div>
          <label className="block space-y-1">
            <span className="text-xs font-semibold text-pink-600">Catatan</span>
            <input className="field" value={note} onChange={(e) => setNote(e.target.value)} placeholder="mis. shift malam, janji dokter" data-testid="day-note-input" />
          </label>
          <EventMini date={date} events={events} onAdd={eventsApi.add} onDelete={eventsApi.remove} />
          <Button onClick={save} className="w-full rounded-full h-11" data-testid="day-save-btn">Simpan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
